import { Alert, Button, ButtonGroup, Col, Container, Form, InputGroup, Row, Tab, Tabs, OverlayTrigger, Popover } from "react-bootstrap";
import Table from 'react-bootstrap/Table';
import { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faCheck, 
    faTimes,
    faSave,
    faPencilAlt,
    faRefresh,
    faCodeCompare,
    faCheckCircle,
    faSpinner,
    faExclamationTriangle,
    faArrowUpRightFromSquare,
    faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import { DiffModal } from "./DiffModal";
import { callWrite, UPDATE_LLM_VISIT_FEEDBACK } from "../../../amplify/appsync/write";
import { PatientVisit } from "../../../amplify/appsync/search-patients";
import { readBinaryFile, getFileUrl } from "../../../amplify/s3/storage";
import { callQuery, LLM_VISIT_FEEDBACK_BY_CSN } from "../../../amplify/appsync/query";
import { LLMVisitFeedback } from "../../../amplify/appsync/query";
import useSetPagePatientVisit from "../../common/GlobalOptionsContext";

export const REASONING_DASHBOARD_PATH = 'reasoning/2025-05-02';

// Add the ANALYSIS_INFO constant
const ANALYSIS_INFO = `Reasoning analysis is done by Claude with the following prompt:

You are an expert tasked with analyzing what risk factors an LLM considered in its reasoning about ED patient revisit risk. Please identify which key modifiable risk factors were considered.

Analyze this model output and identify if it explicitly considers the following risk factors in its reasoning:

    Patient factors:
    - Health literacy / understanding of discharge instructions
    - Support at home
    - Mental health (depression/anxiety/substance use)

    Care factors:
    - Diagnostic accuracy (misdiagnosis/underdiagnosis)
    - Medication issues (adverse reactions/interactions)
    - Specialist followup plans
    - Symptom resolution/control
    - Vital signs at discharge
    - Complex disease interactions (e.g. CHF+CKD)

    Environmental factors:
    - Environmental conditions affecting specific conditions

    Model output to analyze: {row['model_output']}
    
    Please respond in JSON format:
    {{
        "factors_considered": {{
            "health_literacy": <true/false>,
            "home_support": <true/false>,
            "mental_health": <true/false>,
            "diagnostic_accuracy": <true/false>,
            "medication_issues": <true/false>,
            "specialist_followup": <true/false>,
            "symptom_control": <true/false>,
            "discharge_vitals": <true/false>,
            "disease_interactions": <true/false>,
            "environmental": <true/false>
        }},
        "evidence": "Quote relevant text showing which factors were considered",
        "explanation": "Brief explanation of how these factors were incorporated into the reasoning"
    }}`;

interface FactorsConsidered {
    health_literacy: boolean;
    home_support: boolean;
    mental_health: boolean;
    diagnostic_accuracy: boolean;
    medication_issues: boolean;
    specialist_followup: boolean;
    symptom_control: boolean;
    discharge_vitals: boolean;
    disease_interactions: boolean;
    environmental: boolean;
}

interface Evidence {
    [key: string]: string;
}

interface Analysis {
    factors_considered: FactorsConsidered;
    evidence: Evidence;
    explanation: string;
}

interface ParagraphState {
    text: string;
    isAccepted: boolean;
    isEditing: boolean;
    originalText: string;
}

interface ReasoningDashboardProps {
    csn: string;
    tenant?: string;
}

export const ReasoningDashboard = ({
    csn,
    tenant = 'stanford'
}: ReasoningDashboardProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [visitData, setVisitData] = useState<any>(null);
    const [patientDocuments, setPatientDocuments] = useState("");
    const [modelOutput, setModelOutput] = useState("");
    const [originalModelOutput, setOriginalModelOutput] = useState("");
    const [analysis, setAnalysis] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedFactor, setSelectedFactor] = useState<string | null>(null);
    const modelOutputRef = useRef<HTMLDivElement>(null);
    const [currentTab, setCurrentTab] = useState("edit");
    
    // Use state to store the parsed analysis
    const [parsedAnalysis, setParsedAnalysis] = useState<Analysis>({
        factors_considered: {
            health_literacy: false,
            home_support: false,
            mental_health: false,
            diagnostic_accuracy: false,
            medication_issues: false,
            specialist_followup: false,
            symptom_control: false,
            discharge_vitals: false,
            disease_interactions: false,
            environmental: false
        },
        evidence: {},
        explanation: ""
    });
    
    // Use effect to parse analysis when it changes
    useEffect(() => {
        if (analysis && typeof analysis === 'string' && analysis.trim() !== '') {
            try {
                // The regex [\u0000-\u001F\u007F-\u009F] matches all control characters in the ASCII range (0-31 and 127-159), which are not allowed in JSON strings.
                const cleaned = analysis.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
                const parsed = JSON.parse(cleaned);
                setParsedAnalysis(parsed);
            } catch (error) {
                console.error("Error parsing analysis:", error);
                // Keep the default parsed analysis if parsing fails
            }
        }
    }, [analysis]);

    // Split text into paragraphs and initialize state
    const [paragraphs, setParagraphs] = useState<ParagraphState[]>(() => {
        return set_paragraphs(modelOutput);
    });

    useEffect(() => {
        setParagraphs(set_paragraphs(modelOutput));
    }, [modelOutput]);

    const handleParagraphAccept = (index: number) => {
        setParagraphs(prev => prev.map((p, i) => 
            i === index ? { ...p, isAccepted: true, isEditing: false } : p
        ));
    };

    const handleParagraphReject = (index: number) => {
        setParagraphs(prev => prev.map((p, i) => 
            i === index ? { ...p, isAccepted: false, isEditing: false } : p
        ));
    };

    const handleParagraphEdit = (index: number) => {
        setParagraphs(prev => prev.map((p, i) => 
            i === index ? { ...p, isEditing: true } : p
        ));
    };

    const handleParagraphChange = (index: number, newText: string) => {
        setParagraphs(prev => prev.map((p, i) => 
            i === index ? { ...p, text: newText } : p
        ));
    };

    const handleParagraphSave = (index: number) => {
        setParagraphs(prev => prev.map((p, i) => 
            i === index ? { ...p, isEditing: false } : p
        ));
    };

    // Function to fetch dashboard data
    const fetchDashboardData = async () => {
        setIsLoading(true);
        const decoder = new TextDecoder();
        
        try {
            // Fetch UUID mapping first
            const summaryData = await readBinaryFile(
                `${REASONING_DASHBOARD_PATH}/summary.txt`,
                tenant,
                'ed-llm'
            );
            
            const summaryText = decoder.decode(summaryData);
            const summaryLines = summaryText
                .split('\n')
                .filter(line => line.trim())
                .map(line => line.split(','));
            const uuidMapping = Object.fromEntries(
                summaryLines.map(line => [line[1], line[0]])
            );
            
            const uuid = uuidMapping[csn];
            if (!uuid) {
                setErrorMessage("CSN not found in summary file.");
                setIsLoading(false);
                return;
            }
            
            // Fetch the actual data
            const dashboardPath = `${REASONING_DASHBOARD_PATH}/${uuid}.json`;
            const data = await readBinaryFile(
                dashboardPath,
                tenant,
                'ed-llm'
            );
            
            const jsonData = JSON.parse(decoder.decode(data));
            
            // Set visit data and patient documents
            setVisitData(jsonData.visit_data || {});
            setPatientDocuments(jsonData.patient_documents || "");
            setAnalysis(jsonData.factor_analysis?.analysis || "");
            
            // Set originalModelOutput to the file data
            // If jsonData.factor_analysis?.model_output doesn't contain \n\n, add it
            let originalData = jsonData.factor_analysis?.model_output || "";
            if (!originalData.includes('\n\n')) {
                // if originalData includes \n, replace it with \n\n
                if (originalData.includes('\n')) {
                    originalData = originalData.replace(/\n/g, '\n\n');
                } else {
                    // originalData doesn't contain \n, replace '. ' with '.\n\n'
                    originalData = originalData.replace(/\. /g, '.\n\n');
                }
            }
            
            setOriginalModelOutput(originalData);
            
            // Set model output initially to original, will be updated if edited version exists in the database
            setModelOutput(originalData);
            await fetchSavedModelOutput();
            
        } catch (error) {
            console.error("Error fetching data:", error);
            setErrorMessage("Could not load data. Check that you are on the Stanford network and if using VPN that your VPN Group is set to either 'CardinalKey-Full' or 'Stanford-Full'");
        } finally {
            setIsLoading(false);
        }
    };

    //Fetch edited model output from the database
    const fetchSavedModelOutput = async () => {
        try {
            console.log("Fetching saved model output for CSN:", csn);
            const feedbackItems = await callQuery({
                operation: LLM_VISIT_FEEDBACK_BY_CSN,
                csn,
            }) as LLMVisitFeedback[];
                        
            if (feedbackItems && feedbackItems.length > 0) {
                // Get all model output feedback items
                const modelOutputFeedbackItems = feedbackItems.filter(
                    (item: LLMVisitFeedback) => 
                        item.feedback_type === 'edit' && 
                        item.feedback_target === 'model_output'
                );
                                
                // Process model output feedback - find the one with the most recent timestamp
                if (modelOutputFeedbackItems.length > 0) {
                    const modelOutputFeedback = modelOutputFeedbackItems
                        .map((item: LLMVisitFeedback) => {
                            const creationTime = getTimeInMs(item.creation_time);
                            const updateTime = getTimeInMs(item.update_time);
                            
                            // Use the newer timestamp
                            const effectiveTime = Math.max(creationTime, updateTime);
                            
                            return {
                                ...item,
                                effectiveTime,
                                isUpdate: updateTime > creationTime
                            };
                        })
                        .sort((a: any, b: any) => b.effectiveTime - a.effectiveTime)[0]; // Sort descending and take the first
                    
                    setModelOutput(modelOutputFeedback.note);
                } else {
                    console.log("No edited model output found, using original");
                }
            } else {
                console.log("No feedback items found, using original model output");
            }
        } catch (error) {
            console.error("Error fetching saved model output:", error);
        }
    };

    const getTimeInMs = (timestamp: any): number => {
        if (timestamp instanceof Date) {
            return timestamp.getTime();
        }
        
        if (typeof timestamp === 'number') {
            return timestamp;
        }
        
        if (typeof timestamp === 'string') {
            // Try to parse as ISO date first
            const parsedDate = new Date(timestamp);
            if (!isNaN(parsedDate.getTime())) {
                return parsedDate.getTime();
            }
            
            // Try to parse as numeric string
            const numericValue = parseInt(timestamp, 10);
            if (!isNaN(numericValue)) {
                return numericValue;
            }
        }
        
        console.error("Could not parse timestamp:", timestamp);
        return 0; // Use 0 as error value
    };
    
    // Function to save feedback - add more detailed logging
    const handleSaveModelOutput = async (newOutput: string) => {
        setIsSavingNote(true);
        try {
            const result = await callWrite({
                operation: UPDATE_LLM_VISIT_FEEDBACK,
                creation_time: new Date().getTime(),
                csn: csn,
                mrn: "", // Not needed
                feedback_target: 'model_output',
                feedback_type: 'edit',
                note: newOutput,
            });
            
            console.log("Save result:", result);
            setModelOutput(newOutput); // Update the local state with saved output
            setParagraphs(prev => prev.map(p => ({...p, originalText: p.text}))); // Update originalText to match current text
        } catch (error) {
            console.error("Error saving model output:", error);
        } finally {
            setIsSavingNote(false);
        }
    };
    
    // Add patientVisit state directly in the component
    const [patientVisit, setPatientVisit] = useState<PatientVisit>();
    useSetPagePatientVisit(patientVisit);
    
    // Add function to fetch patient visit data
    const fetchPatientVisit = async () => {
        try {
            const patients = await callQuery({
                operation: LLM_VISIT_FEEDBACK_BY_CSN,
                csn: csn,
            });
            
            if (Array.isArray(patients) && patients.length > 0) {
                setPatientVisit(patients[0]);
            } else {
                setPatientVisit(undefined);
            }
        } catch (error) {
            console.error("Error fetching patient visit:", error);
            setPatientVisit(undefined);
        }
    };
    
    // Update useEffect to also fetch patient visit data
    useEffect(() => {
        if (csn) {
            fetchDashboardData();
            fetchPatientVisit(); // Add this line to fetch patient visit data
        }
    }, [csn, tenant]);
    
    if (isLoading) {
        return (
            <Row style={{textAlign: 'center'}}>
                <Col md={12}>
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading dashboard data...
                </Col>
            </Row>
        );
    }
    
    if (errorMessage) {
        return (
            <Row style={{textAlign: 'center'}}>
                <Col md={12}>
                    <Alert variant="danger">
                        <FontAwesomeIcon icon={faExclamationTriangle} /> {errorMessage}
                    </Alert>
                </Col>
            </Row>
        );
    }

    // Create a mapping of evidence text to reasoning type and track which factors have evidence
    const evidenceMap = new Map<string, string>();
    const factorsWithEvidence = new Set<string>();

    // Function to escape HTML tags
    const escapeHtml = (unsafe: string) => {
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    };

    // Process evidence mapping
    Object.entries(parsedAnalysis.evidence).forEach(([reasoningType, evidence]) => {
        if (evidence.includes('...')) {
            const [start, end] = evidence.split('...');
            const startTrimmed = start.trim();
            const endTrimmed = end.trim();
            
            // The problem is here - we need to escape regex special characters in the search strings
            const escapedStartTrim = startTrimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const escapedEndTrim = endTrimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            
            // Use a more flexible approach to find the text in modelOutput
            const startRegex = new RegExp(escapedStartTrim, 'i');
            const endRegex = new RegExp(escapedEndTrim, 'i');
            
            const startMatch = startRegex.exec(modelOutput);
            const endMatch = endRegex.exec(modelOutput);
            
            if (startMatch && endMatch && startMatch.index < endMatch.index) {
                const fullMatch = modelOutput.substring(
                    startMatch.index, 
                    endMatch.index + endMatch[0].length
                );
                evidenceMap.set(escapeHtml(fullMatch), reasoningType);
                factorsWithEvidence.add(reasoningType);
            }
        } else {
            // For direct matches, we should also use a more flexible approach
            try {
                const escapedEvidence = escapeHtml(evidence);
                evidenceMap.set(escapedEvidence, reasoningType);
                factorsWithEvidence.add(reasoningType);
            } catch (e) {
                console.error("Error processing evidence:", e);
            }
        }
    });

    const handleFactorClick = (factor: string) => {
        if (!factorsWithEvidence.has(factor)) return;
        
        const newFactor = selectedFactor === factor ? null : factor;
        setSelectedFactor(newFactor);

        if (newFactor && modelOutputRef.current) {
            setTimeout(() => {
                const highlightElement = modelOutputRef.current?.querySelector('.evidence-highlight.selected');
                highlightElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        }
    };

    // Render text with evidence highlighting
    const renderOutput = () => {
        let highlightedText = modelOutput;
        
        // First, escape the entire text
        highlightedText = escapeHtml(highlightedText);
        
        // Then add highlighting
        evidenceMap.forEach((reasoningType, evidence) => {
            if (!selectedFactor || reasoningType === selectedFactor) {
                try {
                    // Use a string replacement instead of regex to be safer
                    const plainTextEvidence = evidence.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
                    
                    if (highlightedText.includes(evidence)) {
                        highlightedText = highlightedText.split(evidence).join(
                            `<span class="evidence-highlight ${selectedFactor === reasoningType ? 'selected' : ''}" data-reasoning="${reasoningType.replace(/_/g, ' ')}">${evidence}</span>`
                        );
                    } else if (highlightedText.includes(plainTextEvidence)) {
                        // Try with the unescaped version
                        const escapedPlainText = escapeHtml(plainTextEvidence);
                        highlightedText = highlightedText.split(escapedPlainText).join(
                            `<span class="evidence-highlight ${selectedFactor === reasoningType ? 'selected' : ''}" data-reasoning="${reasoningType.replace(/_/g, ' ')}">${escapedPlainText}</span>`
                        );
                    }
                } catch (e) {
                    console.error("Error highlighting text:", e);
                }
            }
        });

        return highlightedText;
    };

    const handleSaveClick = () => {
        const acceptedParagraphs = paragraphs
            .filter(p => p.isAccepted)
            .map(p => p.text)
            .join('\n\n');
        
        handleSaveModelOutput(acceptedParagraphs);
    };

    const resetOriginal = () => {
        // Create new paragraph states from the original model output
        const newParagraphs = set_paragraphs(originalModelOutput);
        
        // Set these as the new paragraphs
        setParagraphs(newParagraphs);
    };

    const hasUnsavedChanges = (
        // Check if any paragraphs differ from their original text or are rejected
        paragraphs.some(p => p.text !== p.originalText || !p.isAccepted) || 
        // Check if current text differs from what's in the database
        paragraphs.filter(p => p.isAccepted).map(p => p.text).join('\n\n') !== modelOutput
    );

    return (
        <Row>
            {/* Left Column - Raw Data */}
            <Col md={4}>
                <Row>
                    <Col>
                        <h5>Raw Data</h5>
                    </Col>
                    <Col className="text-right float-end">
                        {
                            patientVisit && (
                                <Button variant="outline-secondary" size="sm" onClick={() => {
                                    window.open(`/dashboard/${patientVisit.internal_csn}`, "_blank");
                                }}><FontAwesomeIcon icon={faArrowUpRightFromSquare} /> See on Capacity</Button>
                            )
                        }
                    </Col>
                </Row>
                <div className="visit-data-container">
                    <Tabs defaultActiveKey="visit_data">
                        <Tab eventKey="visit_data" title="Visit Info">
                            <RawDataTable data={visitData} />
                        </Tab>
                    </Tabs>
                </div>
            </Col>

            {/* Middle Column - Template */}
            <Col md={4}>
                <h5>Generated Visit Document</h5>
                <pre style={{ whiteSpace: 'pre-wrap' }}>
                    {patientDocuments}
                </pre>
            </Col>

            {/* Right Column - Model Output Editor */}
            <Col md={4}>
                <h5>
                    LLM Output{' '}
                    <OverlayTrigger
                        trigger="click"
                        rootClose
                        placement="left"
                        overlay={
                            <Popover id="analysis-info-popover" style={{ maxWidth: '600px', width: '500px' }}>
                                <Popover.Header as="h6">Analysis Information</Popover.Header>
                                <Popover.Body style={{ 
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '0.9rem',
                                    lineHeight: '1.4'
                                }}>
                                    {ANALYSIS_INFO}
                                </Popover.Body>
                            </Popover>
                        }
                    >
                        <span style={{ display: 'inline-block' }}>
                            <FontAwesomeIcon 
                                icon={faInfoCircle} 
                                style={{ cursor: 'pointer' }}
                            />
                        </span>
                    </OverlayTrigger>
                </h5>
                <div>
                    {/* Factors Considered Table */}
                    <div className="mb-4">
                        <Table size="sm" bordered style={{ width: '100%', tableLayout: 'fixed' }}>
                            <tbody>
                                <tr>
                                    {Object.entries(parsedAnalysis.factors_considered).map(([factor, considered]) => {
                                        const hasEvidence = factorsWithEvidence.has(factor);
                                        return (
                                            <td 
                                                key={factor} 
                                                onClick={() => handleFactorClick(factor)}
                                                className={`factor-cell ${hasEvidence ? 'clickable' : ''} ${selectedFactor === factor ? 'selected' : ''}`}
                                                title={hasEvidence ? 'Click to see evidence in model output' : 'No evidence available'}
                                            >
                                                <div className="factor-text">
                                                    {factor.replace(/_/g, ' ')}
                                                </div>
                                                <div className="factor-status">
                                                    {considered ? 
                                                        <FontAwesomeIcon icon={faCheck} style={{ color: '#28a745' }} /> : 
                                                        <FontAwesomeIcon icon={faTimes} style={{ color: '#dc3545' }} />
                                                    }
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </Table>
                    </div>

                    <Tabs
                        activeKey={currentTab}
                        onSelect={(k) => setCurrentTab(k ?? "")}
                        className="mb-3"
                    >
                        <Tab eventKey="edit" title="Edit">
                            {/* Edit Controls */}
                            <div className="mb-2">
                                {isSavingNote ? (
                                    <Button variant="success" size="sm" disabled>
                                        <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                                    </Button>
                                ) : (
                                    <>
                                        <ButtonGroup>
                                            <Button variant="success" size="sm" onClick={handleSaveClick}>
                                                <FontAwesomeIcon icon={faSave} /> Save Annotations
                                            </Button>
                                            <Button variant="secondary" size="sm" onClick={resetOriginal}>
                                                <FontAwesomeIcon icon={faRefresh} /> Reset All
                                            </Button>
                                        </ButtonGroup>
                                        <div className="float-end d-flex align-items-center">
                                            <DiffModal 
                                                oldText={originalModelOutput} 
                                                newText={paragraphs
                                                    .filter(p => p.isAccepted)
                                                    .map(p => p.text)
                                                    .join('\n\n')} 
                                            />
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="mt-2">
                                <small>
                                    {hasUnsavedChanges ? (
                                        <span className="text-danger">
                                            <i>Not Saved</i>
                                        </span>
                                    ) : (
                                        <span className="text-secondary">
                                            <i><FontAwesomeIcon icon={faCheckCircle} /> All Changes Saved</i>
                                        </span>
                                    )}
                                </small>
                            </div>

                            {/* Paragraphs with Accept/Reject/Edit controls */}
                            <div className="paragraphs-container">
                                {paragraphs.map((paragraph, index) => (
                                    <div key={index} className={`paragraph-block mb-3 ${paragraph.isAccepted ? 'accepted' : ''}`}>
                                        <div className="paragraph-controls mb-2">
                                            <ButtonGroup size="sm">
                                                <Button
                                                    variant={paragraph.isAccepted ? "success" : "outline-success"}
                                                    onClick={() => handleParagraphAccept(index)}
                                                    disabled={paragraph.isEditing}
                                                >
                                                    <FontAwesomeIcon icon={faCheck} /> Accept
                                                </Button>
                                                <Button
                                                    variant={!paragraph.isAccepted ? "danger" : "outline-danger"}
                                                    onClick={() => handleParagraphReject(index)}
                                                    disabled={paragraph.isEditing}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} /> Reject
                                                </Button>
                                                {paragraph.isEditing ? (
                                                    <Button
                                                        variant="primary"
                                                        onClick={() => handleParagraphSave(index)}
                                                    >
                                                        <FontAwesomeIcon icon={faSave} /> Done
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="outline-primary"
                                                        onClick={() => handleParagraphEdit(index)}
                                                    >
                                                        <FontAwesomeIcon icon={faPencilAlt} /> Edit
                                                    </Button>
                                                )}
                                            </ButtonGroup>
                                        </div>
                                        {paragraph.isEditing ? (
                                            <Form.Control
                                                as="textarea"
                                                value={paragraph.text}
                                                onChange={(e) => handleParagraphChange(index, e.target.value)}
                                                className="paragraph-edit-textarea"
                                                rows={Math.max(3, paragraph.text.split('\n').length)}
                                            />
                                        ) : (
                                            <pre className="paragraph-text">
                                                {paragraph.text}
                                            </pre>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Tab>
                        <Tab eventKey="annotation" title="LLM Annotation">
                            <div
                                ref={modelOutputRef}
                                className="model-output-editor"
                                dangerouslySetInnerHTML={{ __html: renderOutput() }}
                            />
                        </Tab>
                    </Tabs>

                    {/* Explanation Box */}
                    <div className="explanation-box">
                        <strong>Analysis:</strong> {parsedAnalysis.explanation}
                    </div>
                </div>
            </Col>
        </Row>
    );
};

const RawDataTable = ({ data, columns }: { data: any, columns?: string[] }): JSX.Element => {
    if (columns && Array.isArray(data)) {
        if (!data || data.length === 0) {
            return <Alert variant="info">No data available</Alert>;
        }

        return (
            <Table size="sm" bordered hover responsive>
                <thead>
                    <tr>
                        {columns.map((col, index) => (
                            <th key={index}>{col.replace(/_/g, ' ')}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, rowIndex) => (
                        <tr key={rowIndex}>
                            {columns.map((col, colIndex) => (
                                <td key={`${rowIndex}-${colIndex}`}>
                                    {item[col]?.toString() || ''}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    }

    const renderValue = (value: any): JSX.Element => {
        if (Array.isArray(value)) {
            return (
                <ul className="mb-0 ps-3">
                    {value.map((item, index) => (
                        <li key={index}>{typeof item === 'object' ? renderValue(item) : String(item)}</li>
                    ))}
                </ul>
            );
        } else if (typeof value === 'object' && value !== null) {
            return (
                <Table size="sm" bordered className="mb-0">
                    <tbody>
                        {Object.entries(value).map(([k, v]) => (
                            <tr key={k}>
                                <td className="fw-bold" style={{width: '40%'}}>{k}</td>
                                <td>{typeof v === 'object' ? renderValue(v) : String(v)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            );
        }
        return <span>{String(value)}</span>;
    };

    return (
        <div className="visit-data-wrapper">
            {renderValue(data)}
        </div>
    );
}; 

function set_paragraphs(modelOutput: string) {
    let paragraphTexts = modelOutput.split('\n\n').filter(p => p.trim());

    return paragraphTexts.map((text, i) => ({
        text: text,
        isAccepted: true,
        isEditing: false,
        originalText: text
    }));
}
